import { Component } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { BusinessSector } from "@enums/businessSector.enum";
import { CrmLeadSource } from "@enums/crmLeadSource.enum";
import { CrmLeadStatus } from "@enums/crmLeadStatus.enum";
import { CrmOpportunityStage } from "@enums/crmOpportunityStage.enum";
import { InteractionType } from "@enums/interactionType.enum";
import { GetPropertiesFromEnum } from "@helpers/getPropertiesFromEnum";
import { AdminHeaderButton } from "@interfaces/adminHeaderButton.interface";
import { Client } from "@interfaces/models/client.interface";
import { CrmContact } from "@interfaces/models/crmContact.interface";
import { CrmInteraction } from "@interfaces/models/crmInteraction.interface";
import { CrmLead } from "@interfaces/models/crmLead.interface";
import { CrmOpportunity } from "@interfaces/models/crmOpportunity.interface";
import { UserProfile } from "@interfaces/userProfile.interface";
import { AuthService } from "@services/auth.service";
import { ClientService } from "@services/client.service";
import { CrmContactService } from "@services/crmContact.service";
import { CrmInteractionService } from "@services/crmInteraction.service";
import { CrmLeadService } from "@services/crmLead.service";
import { CrmOpportunityService } from "@services/crmOpportunity.service";
import { FileService } from "@services/file.service";
import { UserProfileService } from "@services/userProfile.service";
import { ToastService } from "angular-toastify";

@Component({
	moduleId: module.id,
	selector: "crm-leads-id",
	styleUrls: ["crmLeads-id.css"],
	templateUrl: "crmLeads-id.html"
})
export class CrmLeadIdAdminComponent {
	businessSectors: any[] = [];
	clients: Client[] = [];

	get leadStateLabel(): string {
		if (!this.item?.isActive) return "Inactive";
		return this.item.clientId ? "Active Customer" : "Active Lead";
	}

	contacts: CrmContact[] = [];
	headerPrimaryButtons: AdminHeaderButton[] = [];
	interactionTypes: any[] = [];
	interactions: CrmInteraction[] = [];
	item: CrmLead = new CrmLead();
	itemId = 0;
	itemType = "CRM Lead";
	leadSources: any[] = [];
	leadStatuses: any[] = [];
	opportunities: CrmOpportunity[] = [];
	opportunityStages: any[] = [];
	popupAddButtonOptions: any;
	popupCloseButtonOptions: any = { onClick: () => this.closePopup(), text: "Close" };
	popupContact: CrmContact = new CrmContact();
	popupInteraction: CrmInteraction = new CrmInteraction();
	popupTitle = "";
	popupType = "";
	popupVisible = false;
	readOnly = true;
	returnUrl: string = location.href.split("/")[3] + "/" + location.href.split("/")[4];
	title: string = "View " + this.itemType;
	userProfile: UserProfile = new UserProfile();
	userProfiles: UserProfile[] = [];

	constructor(private router: Router, private toastService: ToastService, private route: ActivatedRoute, private itemService: CrmLeadService, private clientService: ClientService, private crmContactService: CrmContactService, private crmInteractionService: CrmInteractionService, private crmOpportunityService: CrmOpportunityService, private fileService: FileService, private userProfileService: UserProfileService, private authService: AuthService) {
		this.businessSectors = GetPropertiesFromEnum(BusinessSector);
		this.leadStatuses = GetPropertiesFromEnum(CrmLeadStatus);
		this.leadSources = GetPropertiesFromEnum(CrmLeadSource);
		this.interactionTypes = GetPropertiesFromEnum(InteractionType);
		this.opportunityStages = GetPropertiesFromEnum(CrmOpportunityStage);
		this.buildButtons("view");
		this.deleteContact = this.deleteContact.bind(this);
		this.uploadNda = this.uploadNda.bind(this);
		this.route.params.subscribe((params) => {
			if (params.id !== "create") {
				if (params.id.split("_").length > 1) {
					this.itemId = params.id.split("_")[0];
					this.getItem(this.itemId);
					this.edit();
				} else {
					this.itemId = params.id;
					this.getItem(params.id);
				}
			} else {
				this.create();
			}
		});
		this.getClients();
		this.getUserProfile();
		this.getUserProfiles();
	}

	addNewContactPopup() {
		this.popupVisible = true;
		this.popupContact = new CrmContact();
		this.popupTitle = "Add New Contact";
		this.popupType = "Contact";
		this.popupAddButtonOptions = { onClick: () => this.saveContact(), text: "Add Contact" };
	}

	addNewInteractionPopup() {
		this.popupVisible = true;
		this.popupInteraction = new CrmInteraction();
		this.popupInteraction.interactionDate = new Date();
		this.popupTitle = "Add New Interaction";
		this.popupType = "Interaction";
		this.popupAddButtonOptions = { onClick: () => this.saveInteraction(), text: "Add Interaction" };
	}

	addNewOpportunity() {
		this.router.navigate(["admin/crmOpportunities/create"], { queryParams: { clientId: this.item.clientId, leadId: this.item.id } });
	}

	buildButtons(mode: string) {
		if (mode === "edit") {
			this.headerPrimaryButtons = [
				{ method: "updateItem", text: "Save Changes" },
				{ method: "cancelEditing", text: "View Mode" },
				{ method: "close", text: "Close" }
			];
		} else if (mode === "create") {
			this.headerPrimaryButtons = [
				{ method: "createItem", text: "Save New Lead" },
				{ method: "close", text: "Close" }
			];
		} else {
			this.headerPrimaryButtons = [
				{ method: "edit", text: "Edit" },
				{ method: "close", text: "Close" }
			];
		}
	}

	cancelEditing() {
		this.readOnly = true;
		this.buildButtons("view");
		this.title = "View " + this.itemType;
		this.getItem(this.itemId);
	}

	close() {
		this.router.navigate([this.returnUrl]);
	}

	closePopup() {
		this.popupVisible = false;
	}

	create() {
		this.readOnly = false;
		this.title = "New " + this.itemType;
		this.buildButtons("create");
		this.item.isActive = true;
	}

	createItem() {
		this.itemService.createSingle(this.item)
			.subscribe(
				(res: any) => {
					this.toastService.success("Lead created!");
					this.router.navigate([`${this.returnUrl}/${res.response.id}`]);
				},
				(err) => { console.log(err); this.toastService.error("Something Went Wrong!"); }
			);
	}

	deleteContact(e: any) {
		const result = confirm("Are you sure you want to delete this contact?");
		if (result) {
			this.crmContactService.deleteSingleById(e.row.data.id)
				.subscribe(
					() => { this.toastService.success("Contact deleted!"); this.getContacts(this.itemId); },
					(err) => { console.log(err); this.toastService.error("Something Went Wrong!"); }
				);
		}
	}

	downloadNda() {
		if (!this.item.ndaFileLookupId) return;
		this.fileService.downloadFile(this.item.ndaFileLookupId as any).subscribe(
			(res: any) => {
				const url = window.URL.createObjectURL(res);
				const a = document.createElement("a");
				a.href = url;
				a.download = this.item.ndaFileName || "NDA";
				a.click();
				window.URL.revokeObjectURL(url);
			},
			(err) => { console.log(err); this.toastService.error("Download failed."); }
		);
	}

	edit() {
		this.readOnly = false;
		this.title = "Edit " + this.itemType;
		this.buildButtons("edit");
	}

	generateClient() {
		if (!this.itemId) { this.toastService.error("Save the lead first."); return; }
		this.itemService.generateClientFromLead(this.itemId).subscribe(
			(res: any) => {
				this.toastService.success("Client created!");
				this.getItem(this.itemId);
				this.router.navigate([`admin/clients/${res.response.id}`]);
			},
			(err) => { console.log(err); this.toastService.error("Something Went Wrong!"); }
		);
	}

	getClients() {
		this.clientService.getAll()
			.subscribe(
				(res: any) => { this.clients = res.response.items; },
				(err) => { console.log(err); }
			);
	}

	getContacts(leadId: any) {
		this.crmContactService.getByLeadId(leadId)
			.subscribe(
				(res: any) => { this.contacts = res.response || []; },
				(err) => { console.log(err); }
			);
	}

	getInteractions(leadId: any) {
		this.crmInteractionService.getByLeadId(leadId)
			.subscribe(
				(res: any) => { this.interactions = res.response || []; },
				(err) => { console.log(err); }
			);
	}

	getItem(id: any) {
		this.itemService.getSingleById(id)
			.subscribe(
				(res: any) => {
					this.item = res.response;
					this.getContacts(id);
					this.getInteractions(id);
					this.getOpportunities(id);
				},
				(err) => { console.log(err); this.toastService.error("Something Went Wrong!"); }
			);
	}

	getOpportunities(leadId: any) {
		this.crmOpportunityService.getByLeadId(leadId)
			.subscribe(
				(res: any) => { this.opportunities = res.response || []; },
				(err) => { console.log(err); }
			);
	}

	getUserProfile() {
		this.authService.getCurrentUserProfile().subscribe(
			(res: any) => { this.userProfile = res.response; },
			(err) => { console.log(err); }
		);
	}

	getUserProfiles() {
		this.userProfileService.getAll()
			.subscribe(
				(res: any) => { this.userProfiles = res.response.items; },
				(err) => { console.log(err); }
			);
	}

	headerButtonClick(method: any) {
		// @ts-ignore
		if (this[method]) this[method]();
	}

	saveContact() {
		this.popupContact.crmLeadId = this.item.id;
		this.crmContactService.createSingle(this.popupContact)
			.subscribe(
				() => { this.closePopup(); this.getContacts(this.itemId); this.toastService.success("Contact added!"); },
				(err) => { console.log(err); this.toastService.error("Something Went Wrong!"); }
			);
	}

	saveInteraction() {
		this.popupInteraction.crmLeadId = this.item.id;
		this.popupInteraction.instigatorId = this.userProfile.id;
		this.crmInteractionService.createSingle(this.popupInteraction)
			.subscribe(
				() => {
					this.closePopup();
					this.getInteractions(this.itemId);
					this.toastService.success("Interaction added!");
					if (this.item.status === CrmLeadStatus.New) {
						this.item.status = CrmLeadStatus.Contacted;
						this.itemService.updateSingleById(this.item.id, this.item).subscribe();
					}
				},
				(err) => { console.log(err); this.toastService.error("Something Went Wrong!"); }
			);
	}

	updateItem() {
		this.itemService.updateSingleById(this.item.id, this.item)
			.subscribe(
				() => { this.toastService.success("Lead updated!"); this.cancelEditing(); },
				(err) => { console.log(err); this.toastService.error("Something Went Wrong!"); }
			);
	}

	uploadNda(file: File) {
		const uploadData = new FormData();
		uploadData.append("files", file);
		this.fileService.uploadFile(uploadData).subscribe(
			(res: any) => { this.item.ndaFileLookupId = res.response[0].lookupId; this.item.ndaFileName = res.response[0].name; },
			(err) => { console.log(err); this.toastService.error("Upload failed."); }
		);
	}

	viewOpportunity(e: any) {
		this.router.navigate([`admin/crmOpportunities/${e.data.id}`]);
	}
}
